import { useContext, useEffect, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';

type ProductId = number;
type CampaignId = number;

type ElasticResponse = {
  hits?: {
    hits: ElasticHit[];
  };
};

type ElasticHit = {
  _source: {
    product: { id: ProductId };
    campaign: { id: CampaignId };
  };
};

export type CampaignIdsByProductId = {
  [productId: number]: CampaignId[];
};

export const useCampaignsByProductId = (productIds: number[]) => {
  const [result, setResult] = useState<CampaignIdsByProductId>({});
  const { market } = useContext(AppContext);

  useEffect(() => {
    if (!IS_BROWSER) {
      return;
    }

    const dealsEndpoint = `${market.campaignsBaseUrl}/campaign-${market.code}-*-deals/_search`;
    fetch(dealsEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        _source: ['product.id', 'campaign.id'],
        size: 100,
        query: {
          bool: {
            filter: {
              terms: { 'product.id': productIds },
            },
          },
        },
      }),
    })
      .catch(err => {
        throw new Error(`FetchError: ${err.message} ${dealsEndpoint}`);
      })
      .then(res => res.json())
      .then((res: ElasticResponse) => {
        if (!res.hits?.hits) return;

        const hits = res.hits?.hits.map(hit => ({
          productId: hit._source.product.id,
          campaignId: hit._source.campaign.id,
        }));
        const getCampaignIdsForProduct = (productId: ProductId) =>
          hits
            .filter(hit => hit.productId === productId)
            .map(hit => hit.campaignId);
        const campaignIdsByProductId = hits.reduce(
          (acc, hit) => ({
            ...acc,
            [hit.productId]: getCampaignIdsForProduct(hit.productId),
          }),
          {} as CampaignIdsByProductId
        );

        setResult(campaignIdsByProductId);
      });
  }, [market.campaignsBaseUrl, market.code, productIds]);

  return result;
};
